import React, { useCallback, useEffect, useState } from 'react';
import BellIcon from 'ui/Icons/bell.component.svg';
import { EUserType } from 'services/BackendApi';
import { HidingTooltip } from 'ui/Tooltip';
import { theme } from '../../../tailwind.config';
import { NotificationsCollection } from 'ui/Notifications/NotificationsCollection';
import { DAYS, extractDateFromISOString } from 'utils/date';
import { useBulkNotificationsMarkViewed } from 'services/CrmApi/mutations/useBulkNotificationsMarkViewed';
import { useQueryClient } from '@tanstack/react-query';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { UnviewedNotificationsCount } from 'ui/Notifications/UnviewedNotificationsCount';

interface INotificationsIconProps {
  userType: EUserType;
  showNotificationsIcon: boolean;
}

export const NotificationsIcon: React.FC<INotificationsIconProps> = ({ userType, showNotificationsIcon }) => {
  const queryClient = useQueryClient();
  const { isMobile } = useResponsive();
  const bellClassname =
    'inv-header-icon text-black min-w-8 min-h-8 max-w-8 max-h-8 flex items-center justify-center cursor-pointer hover:bg-gray-10 rounded-full';

  const startDate = new Date(Date.now() - 30 * DAYS).toISOString();
  const endDate = new Date().toISOString();

  const bulkNotificationsMarkViewedMutation = useBulkNotificationsMarkViewed();
  const markAllNotificationsAsViewed = useCallback(async () => {
    const tomorrow = new Date(Date.now() + 1 * DAYS).toISOString();
    await bulkNotificationsMarkViewedMutation.mutateAsync({
      startDate: extractDateFromISOString(startDate),
      endDate: extractDateFromISOString(tomorrow),
      viewed: true,
    });
    queryClient.invalidateQueries({ queryKey: ['unviewed-notifications-count'] });
  }, [bulkNotificationsMarkViewedMutation, startDate, endDate]);

  const notifyOpenClosedState = useCallback(
    (isOpen: boolean) => {
      if (isOpen && isMobile) {
        document.body.style.overflow = 'hidden';
      } else if (!isOpen && isMobile) {
        document.body.style.overflow = '';
      }

      if (!isOpen) {
        markAllNotificationsAsViewed();
      }
    },
    [isMobile, markAllNotificationsAsViewed]
  );

  const renderNotificationsTooltip = useCallback(
    ({ isOpen }: { isOpen: boolean }) => (
      <NotificationsCollection startDate={startDate} endDate={endDate} isOpen={isOpen} />
    ),
    [startDate, endDate]
  );

  return (
    <HidingTooltip
      renderTooltipContent={renderNotificationsTooltip}
      position={isMobile ? 'bottom-screen' : 'bottom-left'}
      showOnClick
      hideOnClick
      hideOnClickOutside
      notifyOpenClosedState={notifyOpenClosedState}
    >
      <div className={bellClassname}>
        <span className="inv-header-notifications flex">
          <BellIcon className="inv-header-notifications text-black w-[14px]" fill={theme.colors['black']} />
        </span>
        <UnviewedNotificationsCount showUnviewedNotificationsCount={showNotificationsIcon} />
      </div>
    </HidingTooltip>
  );
};
